import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo, personJsonLd } from "@/lib/seo";
import { IMAGES } from "@/lib/assets";
import { TestimonialCard, ServiceCard, SectionLabel } from "@/components/shared";
import Newsletter from "@/components/Newsletter";
import { formatDate, formatINR } from "@/lib/format";
import { ArrowRight, MessageCircle, MapPin, Users, Clock } from "lucide-react";

const steps = [
  { n: "01", t: "Enquire", d: "You reach out. A few short questions help us find the right starting point together." },
  { n: "02", t: "The session", d: "In a private, held space we map your system — so the hidden dynamic can become visible and begin to move." },
  { n: "03", t: "Integration", d: "The days that follow do their own quiet work. I share gentle guidance on what to expect." },
];

export default function Home() {
  useSeo({
    title: "Family Constellation Therapy in Ludhiana & Online",
    description: "Family & Systemic Constellation therapy with Arjunna Soni — grounded, confidential, non-mystical. 7+ years, 1,000+ clients. In Ludhiana and online worldwide.",
    path: "/",
    jsonLd: personJsonLd(),
  });
  const [data, setData] = useState({ services: [], testimonials: [], workshops: [], posts: [], content: {} });

  useEffect(() => {
    Promise.all([
      api.get("/services"),
      api.get("/testimonials", { params: { featured: true } }),
      api.get("/workshops", { params: { filter: "upcoming" } }),
      api.get("/posts", { params: { page: 1 } }),
      api.get("/site-content"),
    ]).then(([s, t, w, p, c]) => {
      setData({
        services: s.data, testimonials: t.data.slice(0, 3),
        workshops: w.data.slice(0, 2), posts: p.data.posts.slice(0, 3), content: c.data,
      });
    }).catch(() => {});
  }, []);

  const hero = data.content.hero || {};
  const cred = data.content.credibility || {};
  const patterns = data.content.patterns || { items: [] };

  return (
    <div>
      {/* HERO */}
      <section className="container-page pt-10 pb-16 md:pt-16 md:pb-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <SectionLabel>Family & Systemic Constellation Therapy</SectionLabel>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-earth leading-[1.08]" style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>
              {hero.headline || "The pattern you keep repeating may not have started with you."}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-earth/70 max-w-xl">
              {hero.subhead}
            </p>
            <h2 className="mt-4 text-lg md:text-xl text-gold-dark font-display">
              Family Constellation Therapy in Ludhiana &amp; Online
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/book" className="btn-gold" data-testid="hero-book-btn">{hero.primaryCta || "Book a Session"}</Link>
              <Link to="/what-is-family-constellation" className="btn-outline" data-testid="hero-learn-btn">
                {hero.secondaryCta || "What is this work?"}
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-in">
            <div className="absolute inset-0 translate-x-4 translate-y-4 border border-gold rounded-lg" />
            <img
              src={IMAGES.hero}
              alt="A calm, spacious room prepared for constellation work"
              width="1200" height="900"
              className="relative w-full aspect-[4/3] object-cover rounded-lg"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="border-y hairline bg-surface-card">
        <div className="container-page py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x-0 md:divide-x divide-hairline">
          {[
            { icon: Clock, v: cred.years || "7+", l: "Years of practice" },
            { icon: Users, v: cred.clients || "1,000+", l: "Clients served" },
            { icon: MapPin, v: cred.location || "Ludhiana & Online", l: "Where we work" },
            { icon: Users, v: cred.formats || "Individual & Group", l: "Formats" },
          ].map((c, i) => (
            <div key={i} className="px-4">
              <div className="text-2xl md:text-3xl text-gold-dark font-display" style={{ fontWeight: 600 }}>{c.v}</div>
              <div className="label-caps mt-1 text-earth/60">{c.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PATTERNS */}
      <section className="container-page py-16 md:py-24">
        <div className="max-w-2xl mb-10">
          <SectionLabel>Does this sound familiar?</SectionLabel>
          <h2 className="text-3xl md:text-5xl text-earth leading-tight" style={{ fontWeight: 600 }}>
            {patterns.title || "Does this sound familiar?"}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border hairline rounded-lg overflow-hidden">
          {patterns.items.map((p, i) => (
            <div key={i} className="bg-parchment p-7 hover:bg-surface-card transition-colors" data-testid={`pattern-${i}`}>
              <span className="text-gold-dark font-display text-2xl" style={{ fontWeight: 600 }}>{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 text-lg text-earth">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS IT */}
      <section className="bg-surface-card border-y hairline py-16 md:py-24">
        <div className="container-page grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionLabel>The work, in plain words</SectionLabel>
            <h2 className="text-3xl md:text-4xl text-earth" style={{ fontWeight: 600 }}>What is Family Constellation work?</h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg md:text-xl text-earth/80 leading-relaxed">
              It is a way of looking at the whole system a person belongs to — their family, and the generations behind them —
              rather than the individual alone. Often a difficulty we carry has its roots in something unspoken or unresolved
              in that system. Constellation work makes those hidden dynamics visible, gently, so that they can begin to move.
              It is experiential, grounded, and never mystical.
            </p>
            <Link to="/what-is-family-constellation" className="inline-flex items-center gap-2 mt-6 font-semibold text-gold-dark hover:gap-3 transition-all">
              Read the full explanation <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-page py-16 md:py-24">
        <SectionLabel>How it works</SectionLabel>
        <h2 className="text-3xl md:text-5xl text-earth mb-12" style={{ fontWeight: 600 }}>Three gentle steps</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div key={s.n} className="relative">
              <div className="text-5xl text-gold/40 font-display" style={{ fontWeight: 600 }}>{s.n}</div>
              <h3 className="text-2xl text-earth mt-2 mb-2">{s.t}</h3>
              <p className="text-earth/70">{s.d}</p>
            </div>
          ))}
        </div>
        <Link to="/how-it-works" className="inline-flex items-center gap-2 mt-10 font-semibold text-gold-dark hover:gap-3 transition-all">
          See the full process <ArrowRight size={18} />
        </Link>
      </section>

      {/* SERVICES */}
      <section className="bg-surface-card border-y hairline py-16 md:py-24">
        <div className="container-page">
          <div className="flex items-end justify-between mb-10">
            <div>
              <SectionLabel>Ways to work together</SectionLabel>
              <h2 className="text-3xl md:text-5xl text-earth" style={{ fontWeight: 600 }}>Services</h2>
            </div>
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 font-semibold text-gold-dark hover:gap-3 transition-all">
              All services <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.services.map((s) => <ServiceCard key={s.service_id} s={s} />)}
          </div>
        </div>
      </section>

      {/* UPCOMING WORKSHOPS */}
      {data.workshops.length > 0 && (
        <section className="container-page py-16 md:py-24">
          <SectionLabel>Coming up</SectionLabel>
          <h2 className="text-3xl md:text-5xl text-earth mb-10" style={{ fontWeight: 600 }}>Upcoming workshops</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {data.workshops.map((w) => (
              <Link key={w.workshop_id} to={`/workshops/${w.slug}`} className="group bg-surface-card border hairline rounded-lg p-7 flex flex-col hover:border-gold transition-all hover:-translate-y-1" data-testid={`home-workshop-${w.slug}`}>
                <div className="flex items-center gap-3 label-caps text-earth/60 mb-3">
                  <span>{formatDate(w.startDate)}</span>
                  <span className="text-gold">•</span>
                  <span>{w.isOnline ? "Online" : w.venue?.city}</span>
                </div>
                <h3 className="text-2xl text-earth mb-2 group-hover:text-gold-dark transition-colors">{w.title}</h3>
                <p className="text-earth/70 flex-1">{w.subtitle}</p>
                <div className="flex items-center justify-between pt-5 mt-4 border-t hairline">
                  <span className="text-xl text-earth font-display" style={{ fontWeight: 600 }}>{formatINR(w.price)}</span>
                  <span className="text-sm text-gold-dark font-semibold">
                    {w.seatsRemaining <= 5 ? `Only ${w.seatsRemaining} seats left` : "Seats available"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {data.testimonials.length > 0 && (
        <section className="bg-surface-card border-y hairline py-16 md:py-24">
          <div className="container-page">
            <SectionLabel>In their words</SectionLabel>
            <h2 className="text-3xl md:text-5xl text-earth mb-10" style={{ fontWeight: 600 }}>Client stories</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {data.testimonials.map((t) => <TestimonialCard key={t.testimonial_id} t={t} />)}
            </div>
            <Link to="/testimonials" className="inline-flex items-center gap-2 mt-10 font-semibold text-gold-dark hover:gap-3 transition-all">
              Read more stories <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      )}

      {/* BLOG */}
      {data.posts.length > 0 && (
        <section className="container-page py-16 md:py-24">
          <SectionLabel>From the journal</SectionLabel>
          <h2 className="text-3xl md:text-5xl text-earth mb-10" style={{ fontWeight: 600 }}>Recent writing</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {data.posts.map((p) => (
              <Link key={p.post_id} to={`/resources/${p.slug}`} className="group" data-testid={`home-post-${p.slug}`}>
                <div className="label-caps text-earth/50 mb-2">{p.categories?.[0]} · {p.readingTime} min read</div>
                <h3 className="text-2xl text-earth group-hover:text-gold-dark transition-colors leading-snug">{p.title}</h3>
                <p className="mt-2 text-earth/70">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* NEWSLETTER */}
      <section className="container-page py-16">
        <div className="bg-surface-card border hairline rounded-lg p-8 md:p-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="max-w-lg">
            <h2 className="text-2xl md:text-3xl text-earth" style={{ fontWeight: 600 }}>Occasional, thoughtful notes</h2>
            <p className="text-earth/70 mt-2">Writing on patterns, belonging, and the quiet work of resolution. No noise, ever.</p>
          </div>
          <Newsletter source="home" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-earth text-parchment py-20 md:py-28 text-center">
        <div className="container-page max-w-2xl">
          <h2 className="text-3xl md:text-5xl text-parchment" style={{ fontWeight: 600 }}>Begin your journey toward resolution.</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-gold" data-testid="final-book-btn">Book a Session</Link>
            <a href="https://wa.me/919115423336" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 border border-parchment text-parchment font-bold px-6 py-3 rounded-md hover:bg-parchment hover:text-earth transition-all" data-testid="final-whatsapp-btn">
              <MessageCircle size={18} /> Message on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
